import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { leadAPI, crmUserAPI, Lead, CRMUser } from '../../services/crmAPI';
import CRMSubNav from './CRMSubNav';
import './CRM.css';

const LeadsList: React.FC = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [users, setUsers] = useState<CRMUser[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [assignedToFilter, setAssignedToFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [sortBy, setSortBy] = useState('-created_at');

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    loadLeads();
  }, [statusFilter, assignedToFilter, sourceFilter, sortBy]);

  const loadUsers = async () => {
    try {
      const response = await crmUserAPI.getAll();
      setUsers(response.data);
    } catch (err) {
      console.error('Error loading users:', err);
    }
  };

  const loadLeads = async () => {
    try {
      setLoading(true);
      const params: any = { sort: sortBy };
      if (statusFilter) params.status = statusFilter;
      if (assignedToFilter) params.assigned_to = assignedToFilter;
      if (sourceFilter) params.source = sourceFilter;
      if (searchFilter) params.search = searchFilter;

      const response = await leadAPI.getAll(params);
      setLeads(response.data.results);
    } catch (err) {
      console.error('Error loading leads:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    loadLeads();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getStatusClass = (status: string) => {
    return `status-badge ${status.toLowerCase().replace('_', '-')}`;
  };

  return (
    <div className="crm-page">
      <CRMSubNav />

      <div className="crm-header">
        <h1>Leads</h1>
        <div className="crm-header-actions">
          <Link to="/crm/leads/new" className="btn btn-primary">
            + Add Lead
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="filter-group search-input">
          <label>Search</label>
          <input
            type="text"
            className="filter-input"
            placeholder="Search business, contact, city..."
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            onKeyPress={handleKeyPress}
          />
        </div>

        <div className="filter-group">
          <label>Status</label>
          <select
            className="filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="NEW">New Lead</option>
            <option value="RESEARCHING">Researching</option>
            <option value="CONTACTED">Contacted</option>
            <option value="MEETING_SCHEDULED">Meeting Scheduled</option>
            <option value="SAMPLED">Sent Samples</option>
            <option value="NEGOTIATING">Negotiating</option>
            <option value="WON">Won</option>
            <option value="LOST">Lost</option>
            <option value="ON_HOLD">On Hold</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Assigned To</label>
          <select
            className="filter-select"
            value={assignedToFilter}
            onChange={(e) => setAssignedToFilter(e.target.value)}
          >
            <option value="">All Users</option>
            {users.map(user => (
              <option key={user.id} value={user.id}>{user.full_name}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Source</label>
          <select
            className="filter-select"
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
          >
            <option value="">All Sources</option>
            <option value="WEBSITE">Website</option>
            <option value="REFERRAL">Referral</option>
            <option value="COLD_CALL">Cold Call</option>
            <option value="TRADE_SHOW">Trade Show</option>
            <option value="RESEARCH">Research</option>
            <option value="WALK_IN">Walk-in</option>
            <option value="OTHER">Other</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Sort By</label>
          <select
            className="filter-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="-created_at">Newest First</option>
            <option value="created_at">Oldest First</option>
            <option value="business_name">Business A-Z</option>
            <option value="-business_name">Business Z-A</option>
            <option value="next_follow_up">Follow-up Date</option>
            <option value="-last_contact_date">Last Contact</option>
          </select>
        </div>

        <button className="btn btn-primary" onClick={handleSearch}>
          Search
        </button>
      </div>

      {/* Leads Table */}
      {loading ? (
        <div className="loading">Loading leads...</div>
      ) : leads.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📋</div>
          <h3>No leads found</h3>
          <p>Try adjusting your filters or add a new lead.</p>
        </div>
      ) : (
        <div className="leads-table-container">
          <table className="leads-table">
            <thead>
              <tr>
                <th>Business Name</th>
                <th>Contact</th>
                <th>City</th>
                <th>Status</th>
                <th>Assigned To</th>
                <th>Last Contact</th>
                <th>Next Follow-up</th>
              </tr>
            </thead>
            <tbody>
              {leads.map(lead => (
                <tr
                  key={lead.id}
                  onClick={() => navigate(`/crm/leads/${lead.id}`)}
                >
                  <td>
                    <strong>{lead.business_name}</strong>
                    <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                      {lead.business_type_display}
                    </div>
                  </td>
                  <td>
                    <div>{lead.contact_name}</div>
                    <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                      {lead.phone}
                    </div>
                  </td>
                  <td>
                    <div>{lead.city}, {lead.state}</div>
                    {lead.region && (
                      <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                        {lead.region}
                      </div>
                    )}
                  </td>
                  <td>
                    <span className={getStatusClass(lead.status)}>
                      {lead.status_display}
                    </span>
                  </td>
                  <td>{lead.assigned_to || '-'}</td>
                  <td>{formatDate(lead.last_contact_date)}</td>
                  <td>
                    <span className={lead.is_follow_up_overdue ? 'followup-date overdue' : ''}>
                      {formatDate(lead.next_follow_up)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: '1rem', color: '#6b7280', fontSize: '0.9rem' }}>
        Showing {leads.length} lead{leads.length !== 1 ? 's' : ''}
      </div>
    </div>
  );
};

export default LeadsList;
